
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Sparkles, PenTool, Eye, ArrowUpRight, Award } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';

const CATEGORIES = ['All', 'Indian Compliance', 'UAE Compliance', 'Accounting', 'Process', 'Community'];

const Blog: React.FC = () => {
  const { posts, currentUser, registeredUsers, institutionalCALogo } = useApp();
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredPosts = posts.filter(post => {
    const isVisible = currentUser?.isAdmin ? true : post.status === 'published';
    const matchesCategory = activeCategory === 'All' || post.category === activeCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase());
    return isVisible && matchesCategory && matchesSearch;
  });

  return (
    <div className="pt-40 pb-32 min-h-screen bg-slate-50 relative text-slate-900">
      <div className="absolute top-0 left-0 w-full h-[600px] bg-gradient-to-b from-indigo-50 to-transparent pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <header className="mb-20">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-indigo-100 text-indigo-600 text-xs font-bold uppercase tracking-widest mb-8 shadow-sm"
          >
            <Sparkles size={14} className="animate-pulse" />
            <span>Institutional Repository</span>
          </motion.div>
          <div className="flex flex-col lg:flex-row justify-between items-end gap-12">
            <div className="max-w-3xl">
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="font-display font-black text-5xl md:text-8xl text-slate-900 mb-8 tracking-tighter leading-[0.95]"
              >
                Intelligence <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-indigo-800">Perspectives</span>
              </motion.h1>
              <p className="text-slate-500 text-xl font-medium leading-relaxed max-w-xl">
                A curated index of regulatory analysis, financial architecture, and global compliance audits.
              </p>
            </div>
            {currentUser && (
              <Link to="/create-post" className="flex items-center gap-3 px-8 py-4 bg-indigo-600 rounded-2xl font-bold text-white shadow-xl hover:bg-indigo-700 transition-all shadow-indigo-600/20">
                <PenTool size={20} /> Deploy New Insight
              </Link>
            )}
          </div>
        </header>

        {/* Toolbar */}
        <div className="sticky top-24 z-30 mb-16 py-4 px-6 rounded-[2rem] bg-white/80 backdrop-blur-2xl border border-slate-200 flex flex-col lg:flex-row gap-6 items-center justify-between shadow-xl shadow-slate-200/50">
          <div className="flex flex-wrap gap-2 overflow-x-auto no-scrollbar">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-6 py-2 rounded-2xl text-sm font-bold transition-all ${
                  activeCategory === cat ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-500 hover:text-indigo-600 hover:bg-slate-50'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className="relative w-full lg:w-80">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input
              type="text" placeholder="Filter analysis..."
              value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-slate-900 focus:outline-none focus:border-indigo-500 focus:bg-white transition-colors"
            />
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          <AnimatePresence mode="popLayout">
            {filteredPosts.map((post) => {
              const author = registeredUsers.find(u => u.id === post.authorId);
              const isCA = author?.designation?.includes('CA') || author?.designation?.includes('FCA');

              return (
                <motion.article
                  key={post.id}
                  layout
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="group bg-white border border-slate-200 rounded-[3rem] overflow-hidden hover:border-indigo-200 hover:shadow-2xl transition-all h-full flex flex-col shadow-lg shadow-slate-200/50"
                >
                  <Link to={`/blog/${post.id}`} className="block aspect-video overflow-hidden relative">
                    <img 
                      src={post.imageUrl || 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?q=80&w=2000&auto=format&fit=crop'} 
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
                      alt={post.title} 
                    />
                    {isCA && (
                      <div className="absolute top-4 right-4 w-10 h-10 bg-white/90 backdrop-blur-md rounded-xl p-1.5 shadow-xl">
                        <img src={institutionalCALogo} className="w-full h-full object-contain" alt="CA Logo" />
                      </div>
                    )}
                  </Link>
                  <div className="p-10 flex flex-col flex-grow">
                    <div className="flex items-center gap-3 mb-6">
                      <span className="px-3 py-1 bg-indigo-50 border border-indigo-100 text-indigo-600 text-[9px] font-black uppercase tracking-widest rounded-lg">
                        {post.category}
                      </span>
                      <span className="text-[10px] text-slate-400 font-bold uppercase">{post.date}</span>
                    </div>
                    <Link to={`/blog/${post.id}`}>
                      <h3 className="text-2xl font-display font-bold text-slate-900 mb-4 leading-tight tracking-tight group-hover:text-indigo-600 transition-colors">
                        {post.title}
                      </h3>
                    </Link>
                    <p className="text-slate-500 text-sm leading-relaxed mb-10 line-clamp-2 font-medium">
                      {post.excerpt}
                    </p>
                    
                    {/* Author Byline */}
                    <Link to={`/author/${post.authorId}`} className="flex items-center gap-3 mb-10 group/author">
                      <div className="w-8 h-8 rounded-lg bg-indigo-50 border border-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-xs">
                        {author?.name.charAt(0)}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-slate-500 group-hover/author:text-indigo-600 transition-colors">{post.author}</span>
                        {isCA && <Award size={12} className="text-indigo-600" />}
                      </div>
                    </Link>

                    <div className="pt-8 border-t border-slate-100 mt-auto flex items-center justify-between">
                      <div className="flex items-center gap-2 text-slate-400 text-[10px] font-bold">
                        <Eye size={14} /> {post.views} Views
                      </div>
                      <Link to={`/blog/${post.id}`} className="text-indigo-600 font-black uppercase tracking-widest text-[10px] flex items-center gap-2 group/link">
                        Read Analysis <ArrowUpRight size={14} className="group-hover/link:translate-x-1 group-hover/link:-translate-y-1 transition-transform" />
                      </Link>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Blog;
